<template lang="pug">
  .transmitter-sensors
    .header
      el-button.dark-green-btn(
        @click="handleConfigureSensors"
      ) {{ $t('transmitters.manange_sensors') }}

    template(v-if="hasSensors")
      el-radio-group.mb-3(
        v-model="activeSensorId"
        fill="#2e7d32"
        text-color="#ffffff"
      )
        el-radio-button(
          v-for="sensor in transmitter.soil_sensors"
          :key="sensor.id"
          :label="sensor.id"
        ) {{ sensorLabel(sensor) }}

      .filters
        .fields-info-box(v-if="Array.isArray(fieldsInfoSettings) && fieldsInfoSettings.length > 0")
          .description
            | {{ $t('sensors.fields_info_settings.actions.description.optimal_ranges', { field: transmitter.field_name }) }}
          .fields-summary
            span(
              v-for="item in fieldsInfoSettings"
              :key="item.id"
              class="field-tag"
            )
              | {{ item.info_type.toUpperCase() }}: {{ item.range1s }} - {{ item.range1e }} {{ item.info_type_unit }}
        FilterDatePicker(
          v-model="dateRange"
          :resetSignal="resetKey"
          @onChange="handleChange"
        )
        el-button.el-icon-refresh-left.mr-2.cursor-pointer(
          style="font-size: 35px; margin-right: 0px;"
          @click="reloadData()"
        )
      br
      SensorChart(
        v-if="sensorInfos"
        v-loading="loadingStatistics"
        :chartData="sensorInfos"
        @onZoom="handleZoom"
      )

    .no-sensors(v-else)
      span {{ $t('transmitters.status.no_sensors') }}
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import SensorChart from "@components/charts/SensorChart.vue";
import FilterDatePicker from "@components/_shared/FilterDatePicker";
import { routerUtils } from "@utils/router";

export default {
  name: "TransmitterSensors",
  mixins: [routerUtils],

  components: { SensorChart, FilterDatePicker },

  data() {
    return {
      activeSensorId: null,
      sensorInfos: null,
      dateRange: [],
      fieldsInfoSettings: [],
      loadingStatistics: false,
      resetKey: null,
      sensorInfoCache: {},
    };
  },

  computed: {
    ...mapState("transmitter", ["transmitter"]),

    hasSensors() {
      return this.transmitter && this.transmitter.soil_sensors && this.transmitter.soil_sensors.length > 0;
    },

    activeSensor() {
      if (!this.hasSensors) return null;
      return this.transmitter.soil_sensors.find((s) => s.id === this.activeSensorId);
    },
  },

  watch: {
    activeSensorId() {
      this.handleChange(this.dateRange);
    },
  },

  async created() {
    if (this.hasSensors) {
      this.activeSensorId = this.transmitter.soil_sensors[0].id;
    }
    await this.handleGetFieldInfoSettings();
  },

  methods: {
    ...mapActions("sensorInfo", ["getSoilSensorInfo"]),
    ...mapActions("field", ["getFieldInfoSettings"]),
    ...mapMutations("dialog", ["hideTransmitterSensorsDialog"]),

    cacheKey(sensorId, values) {
      return `${sensorId}:${values[0]}:${values[1]}`;
    },

    sensorLabel(sensor) {
      console.log(sensor);
      return `${sensor.name} (${this.renderSensorType(sensor)})` || `${sensor.sensor_number} (${this.renderSensorType(sensor)})`;
    },

    renderSensorType(sensor){
      if(sensor.sensor_type)
        switch (sensor.sensor_type) {
          case '0e':
            return 'NPK';
          case 'ee':
            return 'THCNPK';
          case 'de':
            return 'THPHNPK';
          case '10':
            return 'PH';
          case 'c0':
            return 'TH';
          case 'e0':
            return 'THC';
          case 'f0':
            return 'THCPH';
          case 'd0':
            return 'THPH';
          case 'fe':
            return 'THCPHNPK';
          default:
            return 'THC';
        }
    },

    reloadData() {
      this.resetKey = Date.now();
      if (this.activeSensorId && this.dateRange.length > 0) {
        delete this.sensorInfoCache[this.cacheKey(this.activeSensorId, this.dateRange)];
      }
      this.handleChange(this.dateRange);
    },

    async handleZoom([start, end]) {
      this.dateRange = [start, end];
      await this.handleChange(this.dateRange);
    },

    async handleChange(values) {
      if (!this.activeSensorId || !values || values.length === 0) {
        this.sensorInfos = [];
        return;
      }

      const key = this.cacheKey(this.activeSensorId, values);

      if (this.sensorInfoCache[key]) {
        this.sensorInfos = this.sensorInfoCache[key];
        return;
      }

      this.loadingStatistics = true;
      const dateRangeComputed = { start_date: values[0], end_date: values[1] };
      const params = `?by_soil_sensor_id=${this.activeSensorId}&by_date_range[start_date]=${dateRangeComputed.start_date}&by_date_range[end_date]=${dateRangeComputed.end_date}`;

      const response = await this.getSoilSensorInfo(params);
      this.sensorInfoCache[key] = response;
      this.sensorInfos = response;
      this.loadingStatistics = false;
    },

    async handleGetFieldInfoSettings() {
      if (!this.transmitter || !this.transmitter.field_id) {
        this.fieldsInfoSettings = [];
        return;
      }

      const response = await this.getFieldInfoSettings(`?by_field_id=${this.transmitter.field_id}`);
      this.fieldsInfoSettings = response.data.map((item) => ({
        id: item.id,
        info_type: item.info_type || '',
        info_type_unit: item.info_type_unit || '',
        range1s: parseFloat(item.range1s) || 0,
        range1e: parseFloat(item.range1e) || 0,
        range1color: item.range1color || '',
        range2s: parseFloat(item.range2s) || 0,
        range2e: parseFloat(item.range2e) || 0,
        range2color: item.range2color || '',
        range3s: parseFloat(item.range3s) || 0,
        range3e: parseFloat(item.range3e) || 0,
        range3color: item.range3color || '',
      }));
    },

    handleConfigureSensors() {
      this.hideTransmitterSensorsDialog();
      this.goToWIthNestedId('transmitters', this.transmitter.id, 'manage_sensors');
    },
  },
};
</script>

<style lang="scss" scoped>
.header {
  display: flex;
  justify-content: flex-end;
  // margin-bottom: 6px;
}

.no-sensors {
  text-align: center;
  color: #909399;
  padding: 40px 0;
}

.fields-info-box {
  border: 1px solid #e1f3d8;
  background-color: #fcfffa;
  padding: 16px;
  border-radius: 10px;
  margin-bottom: 16px;

  .description {
    font-weight: 500;
    font-size: 14px;
    color: #2d3a4a;
    margin-bottom: 0.75rem;
  }

  .fields-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 14px;

    .field-tag {
      background-color: #f6ffed;
      color: #6aa84f;
      padding: 6px 12px;
      border-radius: 8px;
      font-size: 13.5px;
      font-weight: 500;
      white-space: nowrap;
    }
  }
}
</style>
